'use client'

import { useDevice } from '@lune-fe/lune-ui-lib'
import Box from '@mui/material/Box'
import Text from '@mui/material/Typography'
import AccreditedIcon from '@public/icons/icon-accredited.png'
import BiofuelIcon from '@public/icons/lune-icon-biofuel.svg'
import HubIcon from '@public/icons/lune-icon-hub.svg'
import LiveChartIcon from '@public/icons/lune-icon-live-chart.svg'
import RouteIcon from '@public/icons/lune-icon-route.svg'
import ValidationIcon from '@public/icons/lune-icon-validation.svg'
import OutOfTheBoxReporting from '@public/images/logistics/out-of-the-box-reporting.png'
import LappLogo from '@public/images/logos/lapp-logo.png'
import EmissionsHotspots from '@public/images/product/lune-emissions-hotspots.png'
import GroupView from '@public/images/solutions/lune-group-view.png'
import Image from 'next/image'

import AccordionTile from '@/components/AccordionTile'
import CustomerStoryWithTiles from '@/components/CustomerStoryWithTiles'
import GenericHero from '@/components/Hero/GenericHero'
import HeroAnimation from '@/components/Hero/HeroAnimation'
import { enterpriseShippersHero } from '@/components/Hero/heroAnimationConfig'
import LearnMoreTiles from '@/components/LearnMoreTiles'
import LuneButton from '@/components/LuneButton'
import RequestDemoGreen from '@/components/RequestDemoGreen'
import TextWithImageGoingOffScreen from '@/components/TextWithImageGoingOffScreen/TextWithImageGoingOffScreen'
import TwoColumnsWithSquareImage from '@/components/TwoColumnsWithSquareImage'
import GrynLogos from '@/sections/GrynLogos'

const heroAlt =
    "Gryn by Lune group view of Acme Group's logistics suppliers: group emissions of 7,843 tCO₂e, 6 of 8 suppliers delivered, and each supplier's data status and emissions."

export default function Page() {
    const { mdScreen } = useDevice()

    return (
        <>
            <Box>
                <GenericHero
                    titleComponent="h1"
                    animatedDesktopHero={
                        <HeroAnimation config={enterpriseShippersHero} alt={heroAlt} />
                    }
                    backgroundImageMobile={
                        '/images/solutions/lune-hero-enterprise-shippers-mobile.png'
                    }
                    mobileImageSize={{ width: 1800, height: 1800 }}
                    imageAlt={heroAlt}
                    title={<>Actionable logistics CO₂ insights for enterprise</>}
                    description={
                        <>
                            Automate shipment data collection from suppliers and gain accurate CO₂
                            insights & reporting to decarbonise and stay compliant.
                        </>
                    }
                    button={{
                        href: '/request-a-demo',
                        label: 'Request a demo',
                    }}
                    secondaryButton={{
                        href: '/contact-us',
                        label: 'Contact us',
                    }}
                />
                <GrynLogos caption="Trusted by 100+ companies" />
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: mdScreen ? 6 : 9,
                }}
            >
                <Text variant={mdScreen ? 'h5' : 'h4'} sx={{ textAlign: 'center' }}>
                    Why logistics emissions are hard to get right
                </Text>
                <LearnMoreTiles
                    tiles={[
                        {
                            icon: HubIcon,
                            title: "Data you don't hold",
                            description:
                                'Shipment data sits with dozens of carriers and forwarders, in dozens of formats.',
                        },
                        {
                            icon: ValidationIcon,
                            title: 'Weeks of chasing',
                            description:
                                'Teams spend days to weeks requesting files and filling gaps before calculating anything.',
                        },
                        {
                            icon: AccreditedIcon,
                            title: 'Reporting that must hold up',
                            description:
                                'CSRD, Scope 3 and customer requests need figures you can explain in an audit.',
                        },
                        {
                            icon: LiveChartIcon,
                            title: 'Targets to hit',
                            description:
                                'To cut emissions, you need to see which carriers, lanes and modes drive them.',
                        },
                    ]}
                />
            </Box>
            <TwoColumnsWithSquareImage
                title={'Unlock the data you need'}
                paragraph={
                    'Your shipment data is spread across suppliers and systems. We bring it into one place by engaging your suppliers, then validate it and standardise it to GLEC.'
                }
                snippet={
                    <LuneButton
                        label="Learn more about shipment data collection"
                        href="/shipment-data-collection"
                    />
                }
                image={{
                    src: '/videos/grynbylune.mp4',
                    alt: 'Gryn insights and compliance animation',
                }}
                imageOnRight
                smallerImage
            />
            <TwoColumnsWithSquareImage
                title={'Actionable insights & compliance confidence'}
                paragraph={
                    <>
                        {`Use Lune's industry-leading CO₂ intelligence or seamlessly integrate your existing emissions calculations provider to gain Scope 3 compliance and actionable insights to decarbonise your supply chain.`}
                        <br />
                        <br />
                        {`Our methodology is certified by Smart Freight Centre as GLEC Framework and ISO 14083 compliant, with step-by-step explanations behind every figure.`}
                    </>
                }
                snippet={
                    <LuneButton
                        label="Learn more about emissions calculations"
                        href="/emissions-calculations"
                    />
                }
                image={{
                    src: '/gifs/gryn.gif',
                    alt: 'Gryn data collection animation',
                }}
                smallerImage
            />
            <TextWithImageGoingOffScreen
                title="One view from business unit to HQ"
                paragraph="Data from your suppliers, TMS and ERP feeds each business unit, and every business unit rolls up into one group view. Group sustainability gets the full picture without collecting it by hand from each part of the business."
                img={GroupView}
                imgAlt="Lune group view: Acme Group's 7,843 tCO₂e consolidated from four business units, each fed by carriers, TMS, ERP or spreadsheets."
            />
            <TextWithImageGoingOffScreen
                reverse
                title="See where to cut emissions"
                paragraph="Find the carriers, lanes and modes that drive your footprint, consolidated from business unit to group level. Then reduce what you can with biofuel insetting across sea, air, road and rail."
                snippet={
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 2,
                        }}
                    >
                        <LuneButton label="Analytics & reporting" href="/analytics-reporting" />
                        <LuneButton label="Carbon projects" href="/carbon-projects" />
                    </Box>
                }
                img={EmissionsHotspots}
                imgAlt="Lune emissions hotspots view: a treemap of lanes sized by emissions and a ranked list led by Shanghai to Rotterdam ocean freight at 1,412 tCO₂e."
            />
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: mdScreen ? 6 : 9,
                }}
            >
                <Text variant={mdScreen ? 'h5' : 'h4'} sx={{ textAlign: 'center' }}>
                    One platform, from data to decarbonisation
                </Text>
                <LearnMoreTiles
                    tiles={[
                        {
                            icon: HubIcon,
                            title: 'Shipment data collection',
                            description: "Every supplier's data in one validated dataset.",
                            href: '/shipment-data-collection',
                        },
                        {
                            icon: RouteIcon,
                            title: 'Emissions calculations',
                            description: 'Granular, GLEC and ISO 14083 compliant.',
                            href: '/emissions-calculations',
                        },
                        {
                            icon: LiveChartIcon,
                            title: 'Analytics & reporting',
                            description: 'Live KPIs, hotspots and custom reports.',
                            href: '/analytics-reporting',
                        },
                        {
                            icon: BiofuelIcon,
                            title: 'Carbon projects',
                            description: 'Biofuel insetting and high-quality offsetting.',
                            href: '/carbon-projects',
                        },
                    ]}
                />
            </Box>
            <CustomerStoryWithTiles
                light={false}
                textVariant="heading"
                logo={
                    <Image
                        src={LappLogo}
                        width={108}
                        height={22.05}
                        sizes="108px"
                        alt="LAPP logo"
                    />
                }
                text={
                    <>
                        &quot;With Gryn by Lune, we got clear insights into our transport
                        emissions.&quot;
                        <Text
                            component="p"
                            variant={mdScreen ? 'body2' : 'body1'}
                            sx={{ mt: mdScreen ? 3 : 4 }}
                        >
                            Jessica, Performance & Tender Specialist, LAPP
                        </Text>
                    </>
                }
                rightSide={
                    <Image
                        src={OutOfTheBoxReporting}
                        alt="Lune emissions analytics page with an overall data quality score and monthly emissions broken down by transport mode."
                        style={{ width: '100%', height: 'auto' }}
                        sizes="(max-width: 899px) calc(100vw - 48px), (max-width: 1199px) calc(50vw - 192px), (max-width: 1535px) calc(50vw - 264px), 504px"
                    />
                }
            />
            <Box>
                <Text variant={'h6'} sx={{ mb: 5 }}>
                    FAQs
                </Text>
                <AccordionTile
                    title="Which logistics suppliers can you collect data from?"
                    description="Any carrier, forwarder or parcel provider. Lune engages them to upload their data, connects via API, or you import files from your TMS or ERP, in any format."
                />
                <AccordionTile
                    title="Do our suppliers need to use a template?"
                    description="No. Suppliers can share data in their own format. Lune maps and standardises it."
                />
                <AccordionTile
                    title="Can we see emissions by business unit?"
                    description="Yes. Data from every business unit rolls up into one consolidated group view."
                />
                <AccordionTile
                    title="Which transport modes are covered?"
                    description="Sea, air, road, rail and inland waterways, plus logistics sites such as warehouses and terminals."
                />
                <AccordionTile
                    title="Can we keep our existing emissions calculation provider?"
                    description="Yes. You can keep using your existing EcoTransIT subscription if you prefer, or move to Lune's calculation engine."
                />
                <AccordionTile
                    title="What is Gryn by Lune?"
                    description="Gryn is now part of Lune. Gryn by Lune is Lune's solution for enterprise shippers, built on the same platform."
                />
            </Box>
            <RequestDemoGreen />
        </>
    )
}
